// The Gem Exchange screens' pure helpers: no React, no network. The mobile
// rule mirrors backend/kmax_gateway.normalise_mobile; the server re-checks
// everything, these only keep the forms honest.

const PHP = new Intl.NumberFormat("en-PH", { style: "currency", currency: "PHP" });

export function formatPHP(amount) {
  const n = Number(amount);
  return PHP.format(Number.isFinite(n) ? n : 0);
}

export function formatWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-PH", { dateStyle: "medium", timeStyle: "short" });
}

// `fee` is THIS player's fee when the page knows it (the link view's
// next_fee_php, since the fee depends on whether the player has sold before);
// otherwise the config's fee for a sale after the first.
export function payoutFor(gemsInput, config, fee = config?.fee_php) {
  fee = Number(fee ?? 0);
  const min = Number(config?.min_gems ?? 1);
  const max = Number(config?.max_gems ?? Number.MAX_SAFE_INTEGER);
  const text = String(gemsInput ?? "").trim();
  if (!/^\d+$/.test(text)) return { ok: false, payout: 0, reason: "Enter a whole number of gems" };
  const gems = Number(text);
  if (gems < min) return { ok: false, payout: 0, reason: `The minimum is ${min.toLocaleString("en-PH")} gems` };
  if (gems > max) return { ok: false, payout: 0, reason: `The maximum is ${max.toLocaleString("en-PH")} gems` };
  if (gems <= fee) return { ok: false, payout: 0, reason: `Sell more than ${fee} gems to cover the fee` };
  return { ok: true, gems, payout: gems - fee, reason: "" };
}

const isWhole = (v) => Number.isInteger(v) && v >= 0;

// The fee in words: one figure when every sale pays the same, otherwise the
// first sale and the ones after (operator, 2026-10-16). "" when the server did
// not send whole numbers: no figure is ever guessed.
export function feeWords(config) {
  const fee = config?.fee_php;
  const first = config?.first_sale_fee_php ?? fee;
  if (!isWhole(fee) || !isWhole(first)) return "";
  return first === fee
    ? `${formatPHP(fee)} per sale`
    : `${formatPHP(first)} on your first sale, ${formatPHP(fee)} on each sale after`;
}

// The selling rules shown with every sell form (operator, 2026-10-16). The
// figures are the server's; the turnover rule is ePlayQuest's, enforced there.
export function sellRules(config) {
  const rules = [];
  if (isWhole(config?.min_gems) && config.min_gems > 0) {
    rules.push(`Minimum sale: ${config.min_gems.toLocaleString("en-PH")} gems.`);
  }
  const fee = feeWords(config);
  if (fee) rules.push(`The Gem Collector's fee: ${fee}.`);
  rules.push("Play every gem you buy on ePlayQuest once before you sell it.");
  return rules;
}

export function normaliseMobile(raw) {
  let d = String(raw ?? "").replace(/[\s\-().]/g, "");
  if (d.startsWith("+63")) d = `0${d.slice(3)}`;
  else if (d.startsWith("63") && d.length === 12) d = `0${d.slice(2)}`;
  else if (d.startsWith("9") && d.length === 10) d = `0${d}`;
  return /^09\d{9}$/.test(d) ? d : null;
}

// A Treasure Chest Key: 12 of 23456789ABCDEFGHJKMNPQRSTUVWXYZ (no 0, 1, I, L
// or O), shown as XXXX-XXXX-XXXX. Uppercased with spaces and dashes removed,
// as both servers do; null unless what is left is well formed.
const KEY = /^[2-9A-HJKMNP-Z]{12}$/;

export function normaliseKey(raw) {
  if (typeof raw !== "string") return null;
  const key = raw.toUpperCase().replace(/[ -]/g, "");
  return KEY.test(key) ? key : null;
}

export function maskMobile(mobile) {
  return mobile && mobile.length === 11 ? `${mobile.slice(0, 4)}•••${mobile.slice(-4)}` : "•••";
}

export const METHOD_LABEL = { gcash: "GCash", maya: "Maya" };

const WORDS = {
  requested: "Waiting for The Gem Collector",
  review: "Being checked by a person at The Gem Collector",
  approved: "Approved. Sending your pesos",
  sent: "Sent. Waiting for GCash or Maya to confirm",
  paid: "Paid",
  failed: "The payout failed",
  rejected: "Declined",
  unknown: "Checking with the payment provider",
};

export function saleWords(row) {
  if (!row) return "";
  if (row.refund_state === "pending") return "Not paid. Your gems are on their way back to ePlayQuest";
  if (row.refund_state === "blocked") return "Not paid. Returning your gems needs a person, and we are on it";
  if (row.refund_state === "done") {
    return row.state === "failed"
      ? "The payout failed. Your gems are back on ePlayQuest"
      : "Declined. Your gems are back on ePlayQuest";
  }
  if (row.state === "rejected" && row.refund_state === "not_needed") return "Declined. Your gems were not taken";
  if (row.state === "requested" && row.eplq_sale_state !== "settled") return "Taking the gems from your ePlayQuest balance";
  return WORDS[row.state] || "In progress";
}

export function errorText(err, fallback = "Something went wrong") {
  const d = err?.response?.data?.detail;
  if (typeof d === "string" && d) return d;
  if (d && typeof d.message === "string" && d.message) return d.message;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return fallback;
}

export function isGemExchangeStore(store) {
  return store?.system_store === "gem_exchange";
}

export function newRequestId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return `r-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}
